import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import type { ProductDetail } from "@viajero/proto/guest/v1";
import type { PriceQuantities } from "@/lib/courseBookingContext";
import { formatReservationDateTime } from "@/lib/formatReservationDateTime";
import { colors } from "@/theme/colors";
import { CancelPolicySection } from "@/features/reservation/components/CancelPolicySection";

type Props = {
  product: ProductDetail;
  date: string;
  time: string;
  quantities: PriceQuantities;
  totalPrice: number;
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        gap: { xs: 0.25, sm: 2 },
        py: 0.75,
      }}
    >
      <Typography sx={{ color: colors.textMuted, fontSize: 14 }}>{label}</Typography>
      <Typography
        sx={{
          color: colors.textBody,
          fontSize: 16,
          textAlign: { xs: "left", sm: "right" },
          whiteSpace: "pre-line",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * 購入者情報入力画面の「ご購入内容」表示ブロック。
 * コース詳細画面で取得した商品情報と、選択した利用日時・人数を組み合わせて表示する。
 */
export function PurchaseSummarySection({ product, date, time, quantities, totalPrice }: Props) {
  const { t, i18n } = useTranslation();
  const participantSummary = product.prices
    .filter((price) => (quantities[price.priceTypeId.toString()] ?? 0) > 0)
    .map((price) => `${price.name} x ${quantities[price.priceTypeId.toString()]}`)
    .join(" / ");
  // 費用情報 (含まれるもの/含まれないもの) が両方空なら、その手前の divider を出さず
  // separator の二重表示を防ぐ。
  const hasCostInfo = Boolean(product.includedInCost) || Boolean(product.notIncludedInCost);

  return (
    <Box>
      <SummaryRow label={t("buyerInfo.courseName")} value={product.name} />
      <SummaryRow
        label={t("buyerInfo.dateTime")}
        value={formatReservationDateTime(date, time, i18n.language)}
      />
      <SummaryRow label={t("buyerInfo.participants")} value={participantSummary} />
      <SummaryRow label={t("buyerInfo.totalPrice")} value={`¥${totalPrice.toLocaleString()}`} />

      {hasCostInfo && <Divider sx={{ my: 1.5 }} />}

      <SummaryRow label={t("buyerInfo.includedInCost")} value={product.includedInCost} />
      <SummaryRow label={t("buyerInfo.notIncludedInCost")} value={product.notIncludedInCost} />

      <CancelPolicySection />
    </Box>
  );
}
